import React, { useState } from 'react';
import { Tabs, TabList, Tab, TabPanels, TabPanel, Button } from '@chakra-ui/react';
import { useTabContext } from 'context/TabContext';
import Dialog from 'components/Modal/Dialog';

interface TabItem {
  id: number;
  title: string;
  component: React.ReactNode;
}

interface CustomTabsProps {
  dataTabs: TabItem[];
}

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  isLoading = false,
  onClose,
  onConfirm,
}) => {
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footerActions={
        <>
          <Button
            variant="outline"
            color="brand.fontColors.primary"
            mr={3}
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            bg="#0092b0"
            _hover={{ bg: '#00a2c4' }}
            isLoading={isLoading}
            loadingText="A Finalizar"
            colorScheme="blue"
            onClick={onConfirm}
          >
            Confirmar
          </Button>
        </>
      }
    >
      <p>{message}</p>
    </Dialog>
  );
};

const CustomTabs: React.FC<CustomTabsProps> = ({ dataTabs }) => {
  const { activeTab, setActiveTab, enabledTabs, setEnabledTabs, emptyTabs } = useTabContext();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [nextTab, setNextTab] = useState<number | null>(null);

  const handleTabChange = (index: number) => {
    if (index < activeTab) {
      setNextTab(index);
      setIsDialogOpen(true);
    } else {
      setActiveTab(index);
    }
  };

  const handleConfirmDialog = () => {
    if (nextTab !== null) {
      setActiveTab(nextTab);
      setEnabledTabs(enabledTabs.filter(tab => tab <= nextTab));
      setNextTab(null);
    }
    setIsDialogOpen(false);
  };

  const handleCloseDialog = () => {
    setNextTab(null);
    setIsDialogOpen(false);
  };

  return (
    <>
      <Tabs
        isFitted
        index={activeTab}
        size={'md'}
      >
        <TabList overflowX="auto" overflowY="hidden" whiteSpace="nowrap">
          {dataTabs.map(tab => (
            <Tab key={tab.id} isDisabled={Boolean(!enabledTabs.includes(tab.id) || emptyTabs.includes(tab.id))} onClick={() => handleTabChange(tab.id)}>
              {tab.title}
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {dataTabs.map(tab => (
            <TabPanel key={tab.id}>{tab.component}</TabPanel>
          ))}
        </TabPanels>
      </Tabs>
      <ConfirmationDialog
        isOpen={isDialogOpen}
        title="Tem certeza que pretende voltar?"
        message="Ao voltar para a aba anterior, os dados das abas posteriores serão perdidos. Deseja continuar?"
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDialog}
      />
    </>
  );
};

export default CustomTabs;